/* eslint-disable max-len */
import { useContext, useEffect, useState } from 'react';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useQueryAttributes } from './useQueryAttributes';
import { getQueryArgs, getQueryFromAttributes, getQueryType } from './getQueryFromAttributes';
import { useQuery } from '../../hooks/useQuery';
import QueryContext from '../QueryContext';

const defaultOpts = {
  ssr: true,
  context: {
    timeout: 3000,
  }
};

// these two function will move to experience as options on DataProvider
const useIsMounted = () => {
  const storeId = useStoreId();
  return storeId !== '8119';
};

const shouldSkip = ({ options, mounted }) => {
  // skip renders until
  if (options.variables?.storeId && options.ssr === false && !mounted) {
    // eslint-disable-next-line no-param-reassign
    return true;
  }

  if (options.variables?.storeId === '8119' && mounted) {
    return true;
  }

  return false;
};

const defaultMountFn = ({ mounted }) => mounted;
const defaultSkipFn = ({ skip }) => skip;

export const useDataModel = (queryName, opts = defaultOpts, lazy = false) => {
  const options = {
    ...defaultOpts,
    ...opts,
  };

  const { dataSource, defaultVariables, mounted: mountedFn = defaultMountFn, skip: skipFn = defaultSkipFn } = useContext(QueryContext);

  // method to wait for client render
  const mounted = mountedFn({ mounted: useIsMounted() });
  options.skip = skipFn({ skip: options.skip || shouldSkip({ options, mounted }) });

  if (!options.variables) {
    options.variables = {};
  }

  const { attributes } = useQueryAttributes({ query: queryName, ssr: options.ssr && !mounted });

  const query = getQueryFromAttributes({ name: queryName, attributes, ssr: options.ssr && !mounted });
  const args = getQueryArgs({ attributes, ssr: options.ssr && !mounted, asString: false });
  const queryType = getQueryType({ attributes, name: queryName });

  // if args contain more than all variables
  const missingVariables = Object.keys(args || {}).filter((key) => {
    return !opts.variables?.[key];
  });
  if (missingVariables.length && !options.skip && !lazy) {
    missingVariables.forEach((key) => {
      if (dataSource && key === 'dataSource') {
        options.variables.dataSource = dataSource;
      }
      if (typeof defaultVariables.current[key] !== 'undefined') {
        options.variables[key] = defaultVariables.current[key];
      } else if (args[key]._isRequired) {
        throw new Error(`'${key}' is missing from options.variables in useDataModel hook.
This likely due to one of the following reasons:
  1. A parameter was included when building the component dataModel but not passed into options.variables.
  2. The component dataModel was merged with another component requesting the '${key}' using params(). Implement defaultVaribles.${key} attribute in the QueryProvider to avoid this error.`);
      }
    });
  }

  if (queryType === 'mutation') {
    return useMutation(query, options);
  }

  if (lazy) {
    return useLazyQuery(query, options);
  }

  const response = useQuery(query, options);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && response.data && !response.loading) {
      const { data } = response;

      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.data', {
        type: 'product',
        variables: opts.variables,
        data
      });
    }
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && response.error && !response.loading) {
      const { data } = response;

      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.error', {
        type: 'product',
        variables: opts.variables,
        data
      });
    }
  }, [response.loading]);

  return response;
};
